import React, { useEffect, useRef, useState } from 'react';
import { normalizeImageFocalPoint } from '../../media/imageFocalPoint';
import styles from './ImageFocalPointPicker.module.css';

export default function ImagePointSurface({ src, value, onChange, onDone, label, descriptionId, autoFocus = false, renderMarkers, onReady }) {
  const imageRef = useRef(null);
  const pickerRef = useRef(null);
  const [loadedSrc, setLoadedSrc] = useState(null);
  const point = normalizeImageFocalPoint(value) || { x: 50, y: 50 };
  const ready = loadedSrc === src;

  useEffect(() => {
    if (ready && autoFocus) pickerRef.current?.focus();
  }, [ready, autoFocus]);

  const moveToPointer = (event) => {
    const rect = imageRef.current.getBoundingClientRect();
    if (!rect.width || !rect.height) return;
    onChange(normalizeImageFocalPoint({
      x: Math.round((event.clientX - rect.left) / rect.width * 100),
      y: Math.round((event.clientY - rect.top) / rect.height * 100),
    }));
  };

  const moveWithKeyboard = (event) => {
    const step = event.shiftKey ? 10 : 1;
    const shifts = {
      ArrowLeft: [-step, 0], ArrowRight: [step, 0],
      ArrowUp: [0, -step], ArrowDown: [0, step],
    };
    const shift = shifts[event.key];
    if (!shift) return;
    event.preventDefault();
    event.stopPropagation();
    onChange(normalizeImageFocalPoint({ x: point.x + shift[0], y: point.y + shift[1] }));
  };

  return (
    <button
      ref={pickerRef}
      type="button"
      className={styles.picker}
      aria-label={label}
      aria-describedby={descriptionId}
      disabled={!ready}
      onKeyDown={(event) => {
        if (event.key === 'Escape' && onDone) {
          event.preventDefault();
          event.stopPropagation();
          onDone();
          return;
        }
        moveWithKeyboard(event);
      }}
      onPointerDown={(event) => {
        if (event.button !== 0) return;
        event.currentTarget.setPointerCapture(event.pointerId);
        moveToPointer(event);
      }}
      onPointerMove={(event) => {
        if (event.currentTarget.hasPointerCapture(event.pointerId)) moveToPointer(event);
      }}
      onPointerUp={(event) => {
        if (event.currentTarget.hasPointerCapture(event.pointerId)) {
          moveToPointer(event);
          event.currentTarget.releasePointerCapture(event.pointerId);
        }
      }}
    >
      <span style={{ display: 'block', position: 'relative' }}>
        <img
          ref={imageRef}
          src={src}
          alt="Vue complète"
          className={styles.image}
          draggable={false}
          onLoad={() => { setLoadedSrc(src); onReady?.(true); }}
          onError={() => { setLoadedSrc(null); onReady?.(false); }}
        />
        {ready && (renderMarkers ? renderMarkers(point) : <span className={styles.marker} style={{ left: `${point.x}%`, top: `${point.y}%` }} />)}
      </span>
    </button>
  );
}
